import React from 'react';
import { Check } from 'lucide-react';
import { SeatsChart } from 'services/Seats/types';
import styles from '../PunchupSeatsChartPicker.module.css';

interface ChartItemProps {
	chart: SeatsChart;
	selected?: boolean;
	/**
	 * When true the selection is frozen (e.g. tickets already sold). The currently-selected
	 * chart stays highlighted but cannot be deselected, and non-selected charts are greyed out
	 * and non-interactive.
	 */
	locked?: boolean;
	onSelect?: (chart: SeatsChart) => void;
}

export const ChartItem: React.FC<ChartItemProps> = ({ chart, selected = false, locked = false, onSelect }) => {
	const interactive = !locked;

	const select = () => {
		if (!interactive) return;
		onSelect?.(chart);
	};

	const itemClass = [styles.item, selected ? styles.itemSelected : locked ? styles.itemDisabled : '']
		.filter(Boolean)
		.join(' ');

	// chart.status is the closed union 'NOT_USED' | 'PUBLISHED' — switch on it directly so a typo
	// can't silently style nothing (and the display text is derived separately, below).
	const statusClass = [
		styles.status,
		chart.status === 'PUBLISHED' ? styles.statusPublished : styles.statusNotUsed,
	].join(' ');

	// A real <button> (not a div role="button"): native keyboard activation + disabled semantics, so a
	// locked picker can't be operated by click OR keyboard. The .item CSS carries the button reset.
	return (
		<button type="button" className={itemClass} onClick={select} disabled={!interactive} aria-pressed={selected}>
			{selected && (
				<span className={styles.selectedIndicator}>
					<Check size={14} strokeWidth={2.5} aria-hidden />
				</span>
			)}
			<img className={styles.thumbnail} src={chart.publishedVersionThumbnailUrl} alt={chart.name} loading="lazy" />
			<h3 className={styles.name}>{chart.name}</h3>
			<span className={statusClass}>{chart.status.replace('_', ' ')}</span>
		</button>
	);
};
